import Lucide from '@/components/Base/Lucide';
import Support from '@/pages/Support';
import { toggleAgentWidget } from '@/stores/agentWidgetSlice';
import { useAppDispatch } from '@/stores/hooks';
import { isAgentWidgetOpen } from '@/utils/helper';
import { useEffect, useState } from 'react';
import Button from '../Base/Button';
import { Dialog } from '../Base/Headless';
import Tippy from '../Base/Tippy';

interface SupportSelectionModalProps {
  hideIcon?: boolean;
  externalOpen?: boolean;
  onExternalClose?: () => void;
}

const SupportSelectionModal = ({
  hideIcon,
  externalOpen,
  onExternalClose,
}: SupportSelectionModalProps) => {
  const dispatch = useAppDispatch();
  const [showDialog, setShowDialog] = useState(false);
  const [openSupportSlide, setOpenSupportSlide] = useState(false);
  const [selectedMode, setSelectedMode] = useState<'agent' | 'mail' | null>(
    null
  );

  useEffect(() => {
    if (externalOpen) {
      setSelectedMode(null);
      setShowDialog(true);
      onExternalClose?.();
    }
  }, [externalOpen]);

  const toggleAgentIframe = () => {
    dispatch(toggleAgentWidget());
  };

  const closeAgentIframe = () => {
    if (isAgentWidgetOpen()) {
      toggleAgentIframe();
    }
  };

  const onSelect = (mode: 'agent' | 'mail') => {
    if (mode === 'mail') {
      closeAgentIframe(); // turn off agent iframe if it's open
      setOpenSupportSlide(true);
    } else if (mode === 'agent') {
      toggleAgentIframe();
    }

    setSelectedMode(mode);
    setShowDialog(false);
  };

  return (
    <>
      <Tippy hidden={hideIcon} content='Contact Support' as='button'>
        <Button
          className='flex items-center justify-center text-white border-none cursor-pointer p-2 text-white rounded-full hover:bg-white/5'
          onClick={() => {
            if (selectedMode === 'agent') {
              toggleAgentIframe();
            }
            setSelectedMode(null);
            setShowDialog(true);
          }}
        >
          <Lucide icon='Headphones' />
        </Button>
      </Tippy>
      <Dialog
        size='lg'
        open={showDialog}
        onClose={() => {
          setShowDialog(false);
          setSelectedMode(null);
        }}
      >
        <Dialog.Panel className='p-3 text-center'>
          <Dialog.Title className='flex flex-col gap-3'>
            <div className='flex items-center justify-center w-14 h-14 rounded-full bg-primary/10'>
              <Lucide icon='Headphones' className='w-7 h-7 text-primary' />
            </div>
            <Button
              variant='plain'
              type='button'
              className='absolute right-4 top-4 border-none shadow-none'
              onClick={() => setShowDialog(false)}
            >
              <Lucide icon='X' />
            </Button>

            <div className='text-center'>
              <h2 className='text-xl font-semibold text-slate-800 dark:text-slate-100'>
                How can we help?
              </h2>
              <p className='mt-1.5 text-sm text-slate-500 dark:text-slate-400'>
                Choose how you'd like to get support.
              </p>
            </div>
          </Dialog.Title>
          <Dialog.Description className='flex flex-col gap-4'>
            <div className='w-full grid grid-cols-2 gap-4'>
              <Button
                type='button'
                onClick={() => onSelect('agent')}
                className='group flex flex-col items-center gap-3 p-5 rounded-xl border-2 border-slate-200 dark:border-darkmode-400 hover:border-primary hover:bg-primary/5 transition-all duration-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/40'
              >
                <div className='flex items-center justify-center w-11 h-11 rounded-full bg-slate-100 dark:bg-darkmode-400 group-hover:bg-primary/15 transition-colors duration-200'>
                  <Lucide
                    icon='Bot'
                    className='w-5 h-5 text-slate-500 group-hover:text-primary transition-colors duration-200'
                  />
                </div>
                <div className='text-center'>
                  <div className='text-sm font-semibold text-slate-700 dark:text-slate-200 group-hover:text-primary transition-colors duration-200'>
                    Ask Agent
                  </div>
                  <div className='mt-0.5 text-xs text-slate-400'>
                    Instant AI answers
                  </div>
                </div>
              </Button>

              <Button
                type='button'
                onClick={() => onSelect('mail')}
                className='group flex flex-col items-center gap-3 p-5 rounded-xl border-2 border-slate-200 dark:border-darkmode-400 hover:border-primary hover:bg-primary/5 transition-all duration-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/40'
              >
                <div className='flex items-center justify-center w-11 h-11 rounded-full bg-slate-100 dark:bg-darkmode-400 group-hover:bg-primary/15 transition-colors duration-200'>
                  <Lucide
                    icon='Mail'
                    className='w-5 h-5 text-slate-500 group-hover:text-primary transition-colors duration-200'
                  />
                </div>
                <div className='text-center'>
                  <div className='text-sm font-semibold text-slate-700 dark:text-slate-200 group-hover:text-primary transition-colors duration-200'>
                    Send Mail
                  </div>
                  <div className='mt-0.5 text-xs text-slate-400'>
                    Email our team
                  </div>
                </div>
              </Button>
            </div>
          </Dialog.Description>
        </Dialog.Panel>
      </Dialog>

      <Support open={openSupportSlide} setOpen={setOpenSupportSlide} />
    </>
  );
};

export default SupportSelectionModal;
